import React, {useState, useEffect} from 'react';
import analytics from '@react-native-firebase/analytics';
import {useNavigation, useRoute} from '@react-navigation/native';
import {SafeAreaView, useSafeAreaInsets} from 'react-native-safe-area-context';

import {Button} from '../../../components/Interface/Forms/Buttons/Button';
import {
  SimulacaoCreditoResponse,
  InstallmentOption,
} from '../../../services/creditoMova';

import {
  BackButton,
  BackIcon,
  BannerSubtitle,
  BannerTitle,
  Container,
  Content,
  DisclaimerText,
  Divider,
  Footer,
  OptionCard,
  OptionDetail,
  OptionDetailLabel,
  OptionDetailValue,
  OptionHeader,
  OptionInstallments,
  OptionTotal,
  RadioInner,
  RadioOuter,
  SectionTitle,
  SuccessBanner,
  SuccessIcon,
  SuccessIconWrapper,
  SummaryCard,
  SummaryLabel,
  SummaryRow,
  SummaryValue,
} from './styles';
import {EmprestimoEligibilityGate} from '../../../components/shared/EmprestimoEligibilityGate';

type RouteParams = {
  simulacao: SimulacaoCreditoResponse;
};

function formatCentavos(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function formatValor(centavos: number): string {
  return (centavos).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}


function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

export function EmprestimoSimulacaoResultado() {
  const navigation = useNavigation();
  const route = useRoute();
  const insets = useSafeAreaInsets();
  const {simulacao} = route.params as RouteParams;

  const installmentsOptions =
    simulacao.mova.simulacao.data.installments_options;

  const [selectedOptionId, setSelectedOptionId] = useState<string>(
    installmentsOptions.length > 0
      ? installmentsOptions[0].installments_option_id
      : '',
  );

  const selectedOption = installmentsOptions.find(
    opt => opt.installments_option_id === selectedOptionId,
  );

  useEffect(() => {
  analytics().logEvent('emprestimo_tela_resultado_simulacao');
}, []);

  const valorSolicitado = formatCentavos(simulacao.valor_solicitado_centavos);

  const handleContinue = () => {
  if (!selectedOption) {
    return;
  }

  analytics().logEvent('emprestimo_clicou_continuar_resultado', {
    installments_number: selectedOption.installments_number,
  });

  (navigation as any).navigate('EmprestimoEndereco', {
    simulacao,
    installments_option_id: selectedOption.installments_option_id,
    selectedOption,
  });
};

  const renderOption = (option: InstallmentOption) => {
    const isSelected =
      option.installments_option_id === selectedOptionId;
    const parcela = formatValor(option.installment_amount);
    const total = formatValor(option.installments_sum);

    return (
     <OptionCard
  key={option.installments_option_id}
  selected={isSelected}
  activeOpacity={0.8}
  onPress={() => {
    analytics().logEvent('emprestimo_selecionou_parcelamento', {
      installments_number: option.installments_number,
    });
    setSelectedOptionId(option.installments_option_id);
  }}>
        <OptionHeader>
          <OptionInstallments selected={isSelected}>
            {option.installments_number}x de {parcela}
          </OptionInstallments>
          <RadioOuter selected={isSelected}>
            {isSelected && <RadioInner />}
          </RadioOuter>
        </OptionHeader>
        <OptionTotal>Total: {total}</OptionTotal>
        <Divider />
        <OptionDetail>
          <OptionDetailLabel>Juros a.m.:</OptionDetailLabel>
          <OptionDetailValue>
            {formatRate(option.interest_monthly_rate)}
          </OptionDetailValue>
        </OptionDetail>

        <OptionDetail>
          <OptionDetailLabel>IOF:</OptionDetailLabel>
          <OptionDetailValue>
            {formatCentavos(
              option.installments_option_complement_info.tax_iof_total_value,
            )}
          </OptionDetailValue>
        </OptionDetail>
        <OptionDetail>
          <OptionDetailLabel>1ª parcela:</OptionDetailLabel>
          <OptionDetailValue>
            {formatDate(option.first_installment_date)}
          </OptionDetailValue>
        </OptionDetail>
        <OptionDetail>
          <OptionDetailLabel>Última parcela:</OptionDetailLabel>
          <OptionDetailValue>
            {formatDate(option.last_installment_date)}
          </OptionDetailValue>
        </OptionDetail>
      </OptionCard>
    );
  };

  return (
    <SafeAreaView edges={[]} style={{flex: 1}}>
      <EmprestimoEligibilityGate />
      <Container>

        <Content
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{paddingBottom: 24}}>
          <SuccessBanner style={{paddingTop: insets.top + 8}}>
            <BackButton
              activeOpacity={0.7}
              onPress={() => navigation.goBack()}
              style={{top: insets.top + 8}}>
              <BackIcon name="chevron-left" />
            </BackButton>
            <SuccessIconWrapper>
              <SuccessIcon name="check-circle" />
            </SuccessIconWrapper>
            <BannerTitle>Valor aprovado!</BannerTitle>
            <BannerSubtitle>
              Valor solicitado: {valorSolicitado}
            </BannerSubtitle>
          </SuccessBanner>

          <SummaryCard>
            <SummaryRow>
              <SummaryLabel>Valor líquido:</SummaryLabel>
              <SummaryValue>{valorSolicitado}</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Prazo solicitado:</SummaryLabel>
              <SummaryValue>
                {simulacao.prazo_meses} {simulacao.prazo_meses === 1 ? 'mês' : 'meses'}
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Opções disponíveis:</SummaryLabel>
              <SummaryValue>{installmentsOptions.length}</SummaryValue>
            </SummaryRow>
          </SummaryCard>

          <SectionTitle>Escolha uma opção de parcelamento:</SectionTitle>

          {installmentsOptions.map(renderOption)}

          <DisclaimerText>
            As parcelas já incluem todos os impostos (IOF) e custos. Esta
            simulação é uma pré-aprovação — as condições finais serão
            apresentadas adiante.
          </DisclaimerText>
        </Content>

        <Footer>
          <Button
            title="Continuar com esta opção"
            onPress={handleContinue}
            enabled={!!selectedOption}
          />
        </Footer>
      </Container>
    </SafeAreaView>
  );
}
